import { toastError } from 'components/Organization/Toast';
import { useEffect, useRef } from 'react';

/**
 * useInvitePolling
 * Polls a refetch function until the number of items increases, or max attempts is reached.
 * @param fromInviteForm - boolean flag to start polling
 * @param refetch - async function that returns data with a .membership_trackers array
 * @param data - current data object (should have .membership_trackers)
 * @param onComplete - callback when polling completes (success or max attempts)
 * @param deps - extra dependencies to trigger effect (optional)
 */
export function useInvitePolling({
  startPolling,
  refetch,
  data,
  onComplete,
  maxAttempts = 5,
  interval = 2000,
  deps = []
}: {
  startPolling: boolean;
  refetch: () => Promise<any>;
  data: any;
  onComplete: (success: boolean) => void;
  maxAttempts?: number;
  interval?: number;
  deps?: any[];
}) {
  const initialLengthRef = useRef<number | null>(null);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);

  useEffect(() => {
    if (!startPolling || isPollingRef.current) return;

    isPollingRef.current = true;
    initialLengthRef.current = data?.membership_trackers?.length ?? 0;

    const stopPolling = (success: boolean) => {
      isPollingRef.current = false;
      initialLengthRef.current = null;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      onComplete(success);
    };

    const poll = (attempt = 1) => {
      refetch()
        .then(result => {
          const newLength = result?.data?.membership_trackers?.length ?? 0;
          const initialLength = initialLengthRef.current ?? 0;
          if (newLength > initialLength) {
            stopPolling(true);
            return;
          }
          if (attempt >= maxAttempts) {
            stopPolling(false);
            return;
          }
          pollTimeoutRef.current = setTimeout(
            () => poll(attempt + 1),
            interval
          );
        })
        .catch(() => {
          stopPolling(true);
          toastError({
            message:
              'An error occurred while updating users. Please refresh the page.'
          });
        });
    };

    poll();

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      isPollingRef.current = false;
      initialLengthRef.current = null;
    };
  }, [startPolling, refetch, data, ...deps]);
}
